// ============================================================================
// openWhatsApp.js — Universal, popup-blocker-safe WhatsApp deep-link opener.
//
// iter139 — Fixes "WhatsApp didn't open" bugs on mobile browsers.
//
// Rules:
//   • Always use the `https://wa.me/<digits>?text=<encoded>` URL format. This
//     works on every modern browser, both mobile and desktop. Never use
//     `whatsapp://` which fails on browsers when WhatsApp isn't installed.
//   • The phone number must be digits-only with the country code included.
//     `cleanPhone()` strips spaces, dashes, parentheses and the leading `+`.
//   • The link MUST be opened **synchronously inside the click handler** —
//     iOS Safari and Chrome on Android block popups that come from a
//     Promise callback. Any logging or analytics call must run AFTER the
//     `window.open` (fire-and-forget).
//   • If `window.open()` returns null (the user has popup blockers on),
//     we fall back to `window.location.assign(url)` which navigates the
//     current tab. WhatsApp launches via the OS deep-link handler either
//     way.
// ============================================================================

/** Strip everything except digits — keeps the international country code. */
export function cleanPhone(s) {
  return (s || "").replace(/\D/g, "");
}

/** Build a canonical wa.me URL. Returns "" when the phone is too short to
 *  be valid (less than 8 digits) — never returns a half-broken link. */
export function buildWaUrl(numberOrEmpty, message) {
  const phone = cleanPhone(numberOrEmpty);
  if (!phone || phone.length < 8) return "";
  const text = encodeURIComponent(message || "");
  return `https://wa.me/${phone}?text=${text}`;
}

/** Build a wa.me URL with NO phone number — opens WhatsApp's share picker
 *  so the user can choose any contact / group. Used by "Share via
 *  WhatsApp" buttons. */
export function buildWaShareUrl(message) {
  return `https://wa.me/?text=${encodeURIComponent(message || "")}`;
}

function dispatchToWa(url) {
  let win = null;
  try { win = window.open(url, "_blank"); } catch { win = null; }
  if (!win) {
    try { window.location.assign(url); } catch { /* ignore */ }
  }
  return url;
}

/**
 * Open WhatsApp with a pre-filled message. Must be called SYNCHRONOUSLY
 * inside a click / tap handler (do not `await` anything before this).
 *
 * Returns the URL that was opened, or "" when the phone is invalid.
 */
export function openWhatsApp(number, message) {
  const url = buildWaUrl(number, message);
  if (!url) return "";
  return dispatchToWa(url);
}

/** Open WhatsApp's share picker — same gesture-safe dispatch. */
export function shareViaWhatsApp(message) {
  return dispatchToWa(buildWaShareUrl(message));
}
